import { msg } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { saveAs } from 'file-saver'
import { useCallback, useEffect, useRef, useState } from 'react'
import { tryRefreshAccessToken } from '../../offline/auth-refresh'
import { getAccessTokenFromStorage } from '../../offline/session-store'
import {
  downloadCrashCsvTemplate,
  getCrashCsvTemplates,
  parseImportResponse,
  parseTemplatesResponse,
  postCrashCsvImport
} from '../../services/crash-csv-import'

/**
 * @param {object} props
 * @param {string} props.bearerToken - access token senza prefisso Bearer
 */
export default function CrashCsvImportPanel({ bearerToken }) {
  const { _ } = useLingui()
  const theme = useTheme()
  const fileInputRef = useRef(/** @type {HTMLInputElement|null} */(null))

  const [templatesLoading, setTemplatesLoading] = useState(true)
  const [templatesError, setTemplatesError] = useState(/** @type {string|null} */(null))
  /** @type {Array<{ basicOrganization: number, basicOrganizationName: string, filename: string, manageableOrganizations: Array<{ id: number, shortDescription: string, description: string }> }>} */
  const [templates, setTemplates] = useState([])

  const [selectedTemplateIndex, setSelectedTemplateIndex] = useState(0)
  const [organizationId, setOrganizationId] = useState(/** @type {number|null} */(null))

  const [downloadBusy, setDownloadBusy] = useState(false)
  const [importBusy, setImportBusy] = useState(false)
  const [selectedFile, setSelectedFile] = useState(/** @type {File|null} */(null))
  const [lastTemplateFilename, setLastTemplateFilename] = useState('')

  /** @type {{ batchId: string, summary: { rowsTotal: number, groupsTotal: number, imported: number, duplicatesSkipped: number, errors: number }, details: Array<Record<string, unknown>> }|null} */
  const [importResult, setImportResult] = useState(null)
  const [actionError, setActionError] = useState(/** @type {string|null} */(null))

  const selectedTemplate = templates[selectedTemplateIndex] ?? null

  const resolveHttpMessage = useCallback(
    (status, body) => {
      const fromBody =
        body && typeof body === 'object' && 'message' in body && typeof body.message === 'string'
          ? body.message
          : null
      if (fromBody) return fromBody
      switch (status) {
        case 400:
          return _(msg`The request is invalid or the CSV could not be parsed.`)
        case 401:
          return _(msg`Session expired. Please sign in again.`)
        case 403:
          return _(msg`You are not allowed to import for this organization.`)
        case 413:
          return _(msg`The CSV file is too large.`)
        case 429:
          return _(msg`Too many import requests. Please try again later.`)
        default:
          return _(msg`Request failed`) + ` (${status})`
      }
    },
    [_]
  )

  const loadTemplates = useCallback(async () => {
    setTemplatesLoading(true)
    setTemplatesError(null)
    let token = bearerToken
    try {
      let { status, body } = await getCrashCsvTemplates(token)
      if (status === 401) {
        const refreshed = await tryRefreshAccessToken()
        if (refreshed) {
          token = getAccessTokenFromStorage() || token
          const second = await getCrashCsvTemplates(token)
          status = second.status
          body = second.body
        }
      }
      if (status !== 200) {
        setTemplatesError(resolveHttpMessage(status, body))
        setTemplates([])
        return
      }
      const dto = parseTemplatesResponse(body)
      const list = dto?.templates ?? []
      setTemplates(list)
      setSelectedTemplateIndex(0)
      const firstOrgs = list[0]?.manageableOrganizations ?? []
      setOrganizationId(firstOrgs[0]?.id ?? null)
    } catch (e) {
      setTemplatesError(e instanceof Error ? e.message : _(msg`Failed to load templates.`))
      setTemplates([])
    } finally {
      setTemplatesLoading(false)
    }
  }, [bearerToken, resolveHttpMessage, _])

  useEffect(() => {
    if (!bearerToken) return
    loadTemplates()
  }, [bearerToken, loadTemplates])

  useEffect(() => {
    const tpl = templates[selectedTemplateIndex]
    const orgs = tpl?.manageableOrganizations ?? []
    if (!orgs.length) {
      setOrganizationId(null)
      return
    }
    setOrganizationId((prev) => (orgs.some((o) => o.id === prev) ? prev : orgs[0].id))
  }, [templates, selectedTemplateIndex])

  const handleTemplateChange = (e) => {
    const idx = Number(e.target.value)
    setSelectedTemplateIndex(idx)
    const orgs = templates[idx]?.manageableOrganizations ?? []
    setOrganizationId(orgs[0]?.id ?? null)
    setImportResult(null)
    setActionError(null)
  }

  const handleDownloadTemplate = async () => {
    if (!selectedTemplate) return
    setDownloadBusy(true)
    setActionError(null)
    let token = bearerToken
    try {
      let result = await downloadCrashCsvTemplate(selectedTemplate.basicOrganization, token)
      if (result.status === 401) {
        const refreshed = await tryRefreshAccessToken()
        if (refreshed) {
          token = getAccessTokenFromStorage() || token
          result = await downloadCrashCsvTemplate(selectedTemplate.basicOrganization, token)
        }
      }
      if ('error' in result) {
        setActionError(resolveHttpMessage(result.status, { message: result.error }))
        return
      }
      setLastTemplateFilename(result.filename)
      saveAs(result.blob, result.filename)
    } catch (e) {
      setActionError(e instanceof Error ? e.message : _(msg`Download failed.`))
    } finally {
      setDownloadBusy(false)
    }
  }

  const handlePickFile = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = (e) => {
    const file = e.target.files?.[0]
    setSelectedFile(file ?? null)
    setImportResult(null)
    setActionError(null)
    e.target.value = ''
  }

  const handleImport = async () => {
    if (!selectedFile || organizationId == null) return
    setImportBusy(true)
    setActionError(null)
    setImportResult(null)
    let token = bearerToken
    try {
      let { status, body } = await postCrashCsvImport(selectedFile, organizationId, token)
      if (status === 401) {
        const refreshed = await tryRefreshAccessToken()
        if (refreshed) {
          token = getAccessTokenFromStorage() || token
          const second = await postCrashCsvImport(selectedFile, organizationId, token)
          status = second.status
          body = second.body
        }
      }
      if (status !== 200) {
        setActionError(resolveHttpMessage(status, body))
        return
      }
      const parsed = parseImportResponse(body)
      if (!parsed) {
        setActionError(_(msg`Unexpected response from server.`))
        return
      }
      setImportResult(parsed)
    } catch (e) {
      setActionError(e instanceof Error ? e.message : _(msg`Import failed.`))
    } finally {
      setImportBusy(false)
    }
  }

  const busy = templatesLoading || downloadBusy || importBusy
  const muted = theme.palette.newColors?.gray || theme.palette.text.secondary
  const sectionBg = theme.palette.mode === 'light'
    ? theme.palette.newColors?.lightGray || '#f5f7fb'
    : theme.palette.background.default
  const sectionBorder = theme.palette.newColors?.lightgray || theme.palette.divider

  const statusChip = (status) => {
    if (status === 'imported') {
      return <Chip size="small" label={_(msg`Imported`)} color="success" variant="outlined" />
    }
    if (status === 'duplicate_skipped') {
      return <Chip size="small" label={_(msg`Duplicate skipped`)} color="warning" variant="outlined" />
    }
    if (status === 'error') {
      return <Chip size="small" label={_(msg`Error`)} color="error" variant="outlined" />
    }
    return <Chip size="small" label={status || _(msg`Unknown`)} color="default" variant="outlined" />
  }

  return (
    <Paper
      sx={{
        width: '100%',
        marginTop: 2,
        p: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: sectionBorder
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: muted }}>
          {_(msg`Export template, import crashes`)}
        </Typography>
        {busy && <Chip size="small" color="info" label={_(msg`Working...`)} />}
      </Stack>
      {busy && <LinearProgress sx={{ mb: 2 }} />}
      <Divider sx={{ mb: 1.5 }} />

      {templatesError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {templatesError}
        </Alert>
      )}
      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      {templatesLoading ? (
        <Typography color="text.secondary">{_(msg`Loading templates…`)}</Typography>
      ) : templates.length === 0 ? (
        <Typography color="text.secondary">{_(msg`No template packages available for your account.`)}</Typography>
      ) : (
        <>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              mb: 1.5,
              alignItems: 'flex-start',
              p: 1.5,
              borderRadius: 1.5,
              backgroundColor: sectionBg,
              border: '1px solid',
              borderColor: sectionBorder
            }}
          >
            <FormControl sx={{ minWidth: 260, flex: '1 1 260px' }} size="small" disabled={busy}>
              <InputLabel id="csv-template-type-label">{_(msg`Template type`)}</InputLabel>
              <Select
                labelId="csv-template-type-label"
                label={_(msg`Template type`)}
                value={selectedTemplateIndex}
                onChange={handleTemplateChange}
              >
                {templates.map((tpl, i) => (
                  <MenuItem key={`${tpl.basicOrganization}-${i}`} value={i}>
                    {tpl.basicOrganizationName} ({tpl.filename})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 280, flex: '1 1 280px' }} size="small" disabled={busy}>
              <InputLabel id="csv-org-target-label">{_(msg`Target organization`)}</InputLabel>
              <Select
                labelId="csv-org-target-label"
                label={_(msg`Target organization`)}
                value={organizationId ?? ''}
                onChange={(e) => setOrganizationId(Number(e.target.value))}
              >
                {(selectedTemplate?.manageableOrganizations ?? []).map((org) => (
                  <MenuItem key={org.id} value={org.id}>
                    {org.description}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Tooltip title={lastTemplateFilename || _(msg`Download ZIP template package`)}>
              <span>
                <Button
                  variant="outlined"
                  disabled={busy || !selectedTemplate}
                  onClick={handleDownloadTemplate}
                  sx={{
                    color: muted,
                    borderColor: muted,
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: muted,
                      backgroundColor: sectionBg
                    }
                  }}
                >
                  {_(msg`Download template package`)}
                </Button>
              </span>
            </Tooltip>
          </Box>
          <Typography variant="caption" sx={{ display: 'block', mb: 1.5, color: muted }}>
            {lastTemplateFilename || _(msg`The server will provide the ZIP filename.`)}
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              alignItems: 'center',
              mb: 1.5,
              p: 1.5,
              borderRadius: 1.5,
              backgroundColor: sectionBg,
              border: '1px solid',
              borderColor: sectionBorder
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              hidden
              onChange={handleFileChange}
            />
            <Button sx={{
              color: muted,
              borderColor: muted,
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                borderColor: muted,
                backgroundColor: sectionBg
              }
            }} variant="outlined" disabled={busy} onClick={handlePickFile}>
              {_(msg`Choose CSV file`)}
            </Button>
            {selectedFile ? (
              <Chip
                size="small"
                color="default"
                label={`${selectedFile.name} (${Math.max(1, Math.round(selectedFile.size / 1024))} KB)`}
                onDelete={busy ? undefined : () => setSelectedFile(null)}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                {_(msg`No file selected`)}
              </Typography>
            )}
            <Button
              variant="contained"
              color="secondary"
              disabled={busy || !selectedFile || organizationId == null}
              onClick={handleImport}
            >
              {_(msg`Import CSV`)}
            </Button>
          </Box>
        </>
      )}

      {importResult && (
        <Box sx={{ mt: 3 }}>
          <Divider sx={{ mb: 2 }} />
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            sx={{ mb: 1 }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {_(msg`Import result`)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {_(msg`Batch`)}: {importResult.batchId}
            </Typography>
          </Stack>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Chip label={`${_(msg`Rows`)}: ${importResult.summary.rowsTotal}`} size="small" />
            <Chip label={`${_(msg`Groups`)}: ${importResult.summary.groupsTotal}`} size="small" />
            <Chip label={`${_(msg`Imported`)}: ${importResult.summary.imported}`} size="small" color="success" />
            <Chip
              label={`${_(msg`Duplicates skipped`)}: ${importResult.summary.duplicatesSkipped}`}
              size="small"
              color="warning"
            />
            <Chip label={`${_(msg`Errors`)}: ${importResult.summary.errors}`} size="small" color="error" />
          </Box>

          {importResult.summary.errors === 0 ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              {_(msg`Import completed without errors.`)}
            </Alert>
          ) : (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {_(msg`Import completed with some errors. Check details below.`)}
            </Alert>
          )}

          <TableContainer
            sx={{
              maxHeight: 420,
              border: '1px solid',
              borderColor: sectionBorder,
              borderRadius: 1,
              backgroundColor: sectionBg
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>{_(msg`External key`)}</TableCell>
                  <TableCell>{_(msg`Status`)}</TableCell>
                  <TableCell>{_(msg`Crash ID`)}</TableCell>
                  <TableCell>{_(msg`Reason`)}</TableCell>
                  <TableCell>{_(msg`Message`)}</TableCell>
                  <TableCell>{_(msg`Report hour (UTC)`)}</TableCell>
                  <TableCell>{_(msg`Road (norm)`)}</TableCell>
                  <TableCell>{_(msg`CSV Crash id (norm)`)}</TableCell>
                  <TableCell>{_(msg`Existing crash`)}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {importResult.details.map((row, idx) => (
                  <TableRow key={`${idx}-${String(row.crashExternalKey)}-${String(row.status)}`} hover>
                    <TableCell>{String(row.crashExternalKey ?? '')}</TableCell>
                    <TableCell>{statusChip(String(row.status))}</TableCell>
                    <TableCell>{row.crashId != null ? String(row.crashId) : '—'}</TableCell>
                    <TableCell>{row.reason != null ? String(row.reason) : '—'}</TableCell>
                    <TableCell>{row.message != null ? String(row.message) : '—'}</TableCell>
                    <TableCell>{row.reportDateHour != null ? String(row.reportDateHour) : '—'}</TableCell>
                    <TableCell>{row.roadNameNorm != null ? String(row.roadNameNorm) : '—'}</TableCell>
                    <TableCell>{row.csvCrashIdNorm != null ? String(row.csvCrashIdNorm) : '—'}</TableCell>
                    <TableCell>{row.existingCrashId != null ? String(row.existingCrashId) : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {importResult.details.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {_(msg`No details returned by server.`)}
            </Typography>
          )}
        </Box>
      )}
    </Paper>
  )
}
